import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from './Modal';
import { api } from '../lib/api';
import { RotateCcw } from 'lucide-react';

interface VersionHistoryModalProps {
  open: boolean;
  onClose: () => void;
  chartType: 'base' | 'personal';
  chartId: number;
}

export function VersionHistoryModal({ open, onClose, chartType, chartId }: VersionHistoryModalProps) {
  const queryClient = useQueryClient();

  const { data: versions = [], isLoading } = useQuery({
    queryKey: ['chartVersions', chartType, chartId],
    queryFn: () => api.charts.getVersions(chartType, chartId),
    enabled: open,
  });

  const restoreVersion = useMutation({
    mutationFn: (versionId: number) => api.charts.restoreVersion(chartType, chartId, versionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [chartType === 'base' ? 'baseChart' : 'personalChart', chartId] });
      queryClient.invalidateQueries({ queryKey: ['chartVersions', chartType, chartId] });
      onClose();
      window.location.reload();
    },
  });

  return (
    <Modal open={open} onClose={onClose} title="Version History" maxWidth="max-w-lg">
      {isLoading ? (
        <p className="text-sm text-gray-400 py-4">Loading versions...</p>
      ) : versions.length === 0 ? (
        <p className="text-sm text-gray-400 py-4">No previous versions yet. Versions are created each time you save.</p>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {versions.map((v: any) => (
            <div key={v.id} className="flex items-center justify-between p-3 bg-surface-800 rounded-lg">
              <div>
                <p className="text-sm font-medium">Version {v.versionNumber}</p>
                <p className="text-xs text-gray-500">{new Date(v.createdAt).toLocaleString()}</p>
              </div>
              <button
                onClick={() => { if (confirm(`Restore to version ${v.versionNumber}?`)) restoreVersion.mutate(v.id); }}
                disabled={restoreVersion.isPending}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-400 hover:bg-surface-700 rounded transition-colors"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Restore
              </button>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
